import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsTags, BsPlus } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import Menu from '#globalComponents/Overlay/Menu';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import Tag from '#globalComponents/Tag/Tag';

import { useCreateTag, useUpdateCompanyTags } from '../../../models/tags/mutations';

const exactMatch = (tags, term) => tags.find((t) => t.name.toLowerCase() === term.toLowerCase());

const CompanyTagsCard = ({ company, allTags = [] }) => {
	const tags = company.tags ?? [];
	const updateTags = useUpdateCompanyTags();
	const createTag = useCreateTag();

	const [adding, setAdding] = useState(false);
	const [search, setSearch] = useState('');
	const [creating, setCreating] = useState(false);

	const selectedIds = new Set(tags.map((t) => t.id));
	const term = search.trim();
	const suggestions = allTags.filter(
		(t) => !selectedIds.has(t.id) && t.name.toLowerCase().includes(term.toLowerCase()),
	);
	const canCreate = term.length > 0 && !exactMatch(allTags, term);

	const persist = (nextTags) => updateTags.mutate({ companyId: company.id, tags: nextTags });

	const removeTag = (tagId) => persist(tags.filter((t) => t.id !== tagId));

	const addTag = (tag) => {
		if (!selectedIds.has(tag.id)) persist([...tags, tag]);
		setSearch('');
	};

	const closeAdd = () => {
		setAdding(false);
		setSearch('');
	};

	const handleCreate = async () => {
		if (!canCreate || creating) return;
		setCreating(true);
		try {
			const { tag } = await createTag.mutateAsync({ name: term });
			if (tag) addTag(tag);
		} finally {
			setCreating(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			const match = exactMatch(suggestions, term);
			if (match) addTag(match);
			else if (canCreate) void handleCreate();
		} else if (e.key === 'Escape') {
			closeAdd();
		}
	};

	const addButton = (
		<button
			type="button"
			onClick={() => (adding ? closeAdd() : setAdding(true))}
			title="Tag hinzufügen"
			className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-dashed border-bgBase-600 text-bgBase-800 hover:text-primary-600 hover:border-primary-400 transition-colors"
		>
			<BsPlus className="w-3.5 h-3.5" />
		</button>
	);

	return (
		<Card className="overflow-hidden p-0">
			<div className="px-4 py-2.5 border-b border-bgBase-500 bg-bgBase-300 flex items-center">
				<h4 className="text-xs-head text-text flex items-center gap-1.5">
					<BsTags className="w-3.5 h-3.5 text-bgBase-800" /> Tags
				</h4>
			</div>
			<div className="p-3">
				<div className="flex flex-wrap items-center gap-1">
					{tags.map((tag) => (
						<Tag key={tag.id} label={tag.name} size="sm" hoverRemove onRemove={() => removeTag(tag.id)} />
					))}

					<Popover open={adding} onClose={closeAdd} trigger={addButton} size="s">
						<input
							type="text"
							autoFocus
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder="Tag suchen oder erstellen …"
							className="w-full px-3.5 py-2 text-sm border-b border-bgBase-400 outline-none flex-shrink-0"
						/>
						<Panel.Body padded={false}>
							<Menu>
								{canCreate && (
									<Menu.Item onClick={handleCreate} disabled={creating}>
										<span className="flex items-center gap-1.5">
											<span className="text-bgBase-800">+</span>
											<span>
												„<span className="font-medium">{term}</span>“ erstellen
											</span>
										</span>
									</Menu.Item>
								)}
								{suggestions.map((tag) => (
									<Menu.Item
										key={tag.id}
										onClick={() => addTag(tag)}
										trailing={typeof tag.usageCount === 'number' ? tag.usageCount : undefined}
									>
										{tag.name}
									</Menu.Item>
								))}
								{suggestions.length === 0 && !canCreate && <Menu.Empty>Keine Tags</Menu.Empty>}
							</Menu>
						</Panel.Body>
					</Popover>

					{tags.length === 0 && !adding && <span className="text-xs text-bgBase-800 ml-1">Keine Tags</span>}
				</div>
			</div>
		</Card>
	);
};

CompanyTagsCard.propTypes = {
	company: PropTypes.object.isRequired,
	allTags: PropTypes.array,
};

export default CompanyTagsCard;
